<script setup>
import { ref, watch, computed, onMounted } from "vue"
import { useToastr } from "@/composables/useToastr"
import axiosIns from "@/plugins/axios.js"
import VueSelect from '@/views/frontend/pages/trabaja_en_agv/componentes/vue-select-nativo.vue'

const props = defineProps({
    data: {
        type: Object,
        default: () => ({})
    }
})

const id_applicant = JSON.parse(localStorage.getItem('userData'))?.id_usuario

const formatDate = (date) => {
    if (!date) return ''
    const d = new Date(date)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleDateString('es-PE', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

const ubicacion = (u) => [u?.district, u?.province, u?.departament].filter(Boolean).join(', ')

const us = ref({ ...props.data })
const saving = ref(false)

// Cada tarjeta (Datos personales / Ubicación / Contacto / Información adicional)
// guarda directo al backend apenas confirmas, en vez de acumularse para el botón
// grande "Actualizar datos" (ese solo sirve para Perfil/Experiencia/Educación).
const savePostulanteFields = (fields, successMsg = "Datos actualizados correctamente.✍🏻") => {
    saving.value = true
    return axiosIns({
        method: "post",
        url: "/talentoagv/updApplicant/",
        data: {
            id: id_applicant,
            payload: { postulante: fields },
        }
    })
    .then(function () {
        toastr.success(successMsg, "Operación exitosa.👍🏻")
    })
    .catch(function (error) {
        console.log(error)
        toastr.error("No se pudo guardar. Intenta nuevamente.", "Error")
    })
    .finally(function () {
        saving.value = false
    })
}

// Solo letras (con tildes/ñ) y espacios; sin números ni símbolos.
const onlyLettersInput = (e, field) => {
    us.value[field] = e.target.value.replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]/g, '')
}

// DNI: 8 dígitos. Pasaporte / C.E.: hasta 12 dígitos. Solo números.
const docMaxLength = computed(() => us.value.type_doc === 'DNI' ? 8 : 12)

const onlyDigitsDocInput = (e) => {
    us.value.n_doc = e.target.value.replace(/\D/g, '').slice(0, docMaxLength.value)
}

// Al cambiar el tipo de documento, recorta el N° ya ingresado al nuevo límite.
watch(() => us.value.type_doc, () => {
    if (us.value.n_doc) {
        us.value.n_doc = us.value.n_doc.slice(0, docMaxLength.value)
    }
})

const saveDatosPersonales = () => {
    if (!/^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]+$/.test((us.value.name || '').trim())) {
        toastr.error("El campo Nombres solo debe contener letras.", "Error")
        return
    }
    if (!/^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]+$/.test((us.value.first_surname || '').trim())) {
        toastr.error("El campo Apellido Paterno solo debe contener letras.", "Error")
        return
    }
    if (!/^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]+$/.test((us.value.second_surname || '').trim())) {
        toastr.error("El campo Apellido Materno solo debe contener letras.", "Error")
        return
    }
    if (us.value.type_doc === 'DNI' && !/^\d{8}$/.test(us.value.n_doc || '')) {
        toastr.error("El DNI debe tener 8 dígitos numéricos.", "Error")
        return
    }
    if (us.value.type_doc !== 'DNI' && !/^\d{1,12}$/.test(us.value.n_doc || '')) {
        toastr.error("El N° de Documento debe ser numérico (hasta 12 dígitos).", "Error")
        return
    }

    return savePostulanteFields({
        name: us.value.name,
        first_surname: us.value.first_surname,
        second_surname: us.value.second_surname,
        type_doc: us.value.type_doc,
        n_doc: us.value.n_doc,
        nationality: us.value.nationality,
        birthdate: us.value.birthdate,
        gender: us.value.gender,
        marital_status: us.value.marital_status,
    })
}

const saveUbicacion = () => savePostulanteFields({
    country: us.value.country,
    departament: us.value.departament,
    province: us.value.province,
    district: us.value.district,
})

const onlyDigitsPhoneInput = (e) => {
    us.value.n_phone = e.target.value.replace(/\D/g, '').slice(0, 9)
}

const saveContacto = () => {
    if (!/^\d{9}$/.test(us.value.n_phone || '')) {
        toastr.error("El Celular debe tener 9 dígitos numéricos.", "Error")
        return
    }
    return savePostulanteFields({
        n_phone: us.value.n_phone,
    })
}

const saveInfoAdicional = (field) => savePostulanteFields(
    { [field]: us.value[field] },
    "Información actualizada.✍🏻"
)
const imgApplicant = ref()
const imgPreviewApplicant = ref()
const toastr = useToastr()

const types_docs = ref(['DNI','PASAPORTE','C.E.'])
const genders = ref(['Masculino','Femenino'])
const marital_status = ref(['Soltero','Casado','Divorciad(a)'])

const updProfilePhoto = (event) => {

    const file = event.target.files[0];
    if (file) {
        imgApplicant.value = file;
        imgPreviewApplicant.value = URL.createObjectURL(file);
    }

    const formData = new FormData();

    formData.append('img_applicant', imgApplicant.value);
    formData.append('applicant_id', id_applicant)

    const config = {
        method: "post",
        url: "/talentoagv/updApplicantFile/",
        data: formData
    };

    axiosIns(config)
    .then(function (response) {
        toastr.success("Datos actualizados correctamente.✍🏻", "Operación exitosa.👍🏻");
    })
    .catch(function (error) {
        console.log(error);
    });

}

// --- Ubigeo (país > departamento > provincia > distrito), para el modal de Ubicación ---
const ubigeo = ref([])

onMounted(() => {
    axiosIns({ method: "post", url: "/talentoagv/getUbigeo/" })
    .then((response) => {
        if (response.data && response.data.is_error === false) ubigeo.value = response.data.result
    })
    .catch((error) => console.log(error))
})

// Compara sin tildes ni mayúsculas para reconocer valores ya guardados (ej. "Lambayeque" = "LAMBAYEQUE")
const norm = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()
const findByName = (list, name) => list.find(x => norm(x.nombre) === norm(name))

const buildOptions = (names, current) => {
    const opts = [...names]
    if (current) {
        const i = opts.findIndex(n => norm(n) === norm(current))
        if (i >= 0) opts[i] = current
        else opts.push(current)
    }
    return opts
}

const selPais = computed(() => findByName(ubigeo.value, us.value.country))
const selDepartamento = computed(() => selPais.value ? findByName(selPais.value.departamentos, us.value.departament) : null)
const selProvincia = computed(() => selDepartamento.value ? findByName(selDepartamento.value.provincias, us.value.province) : null)

const countryOptions = computed(() => buildOptions(ubigeo.value.map(p => p.nombre), us.value.country))
const departamentOptions = computed(() => buildOptions(selPais.value ? selPais.value.departamentos.map(d => d.nombre) : [], us.value.departament))
const provinceOptions = computed(() => buildOptions(selDepartamento.value ? selDepartamento.value.provincias.map(p => p.nombre) : [], us.value.province))
const districtOptions = computed(() => buildOptions(selProvincia.value ? selProvincia.value.distritos : [], us.value.district))

const onCountry = (v) => { us.value.country = v; us.value.departament = ''; us.value.province = ''; us.value.district = '' }
const onDepartament = (v) => { us.value.departament = v; us.value.province = ''; us.value.district = '' }
const onProvince = (v) => { us.value.province = v; us.value.district = '' }
const onDistrict = (v) => { us.value.district = v }

watch(
    () => props.data,
    (newVal) => { us.value = { ...newVal } },
    { immediate: true }
)


</script>
<template>
    <div class="stickysidebar">
        <div class="settings-widget w-100 photo-card">
            <div class="row">
                <div class="col-md-12" style="text-align: center; width: 100%;">
                    <img
                    alt="profile image"
                    :src="imgPreviewApplicant || us.s3_url || require('@/assets/img/agv_logo.jpg')"
                    class="avatar-lg rounded-circle profile_photo mt-4"
                    style="width: 70%; height: auto"
                    />

                    <label class="file-upload mt-2" style="border:none;">
                        <i class="feather-upload"></i>&nbsp;Editar foto
                        <input
                        type="file"
                        class="form-control"
                        name="txt-imagen"
                        accept="image/png, image/gif, image/jpeg"
                        @change="updProfilePhoto"
                        />
                    </label>
                </div>
                <div class="col-md-12">
                    <div class="text-muted text-center">
                        <p style="font-size:11px; padding: 0 15px;">
                            La foto debe tener un tamaño
                            de 450x450 y un tamaño máximo de 5mb.
                        </p>
                        <h6>
                            <span style="font-size: 12px;">{{ us ? us.name.split(' ')[0] + ' ' + us.first_surname : ""}}</span>
                            <img src="@/assets/img/icon/verified-badge.svg" class="ms-1" alt="Img" />
                        </h6>
                        <p class="mb-0" style="font-size: 11px;">{{us.email}}</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="settings-widget w-100 profile-summary-card">
            <div class="profile-summary-header">
                <h6 class="profile-summary-title">Datos personales</h6>
                <a href="javascript:void(0);" class="edit-icon-btn" title="Editar datos personales" data-bs-toggle="modal" data-bs-target="#modalDatosPersonales">
                    <i class="feather-edit-2"></i>
                </a>
            </div>
            <div class="profile-summary-row"><span>Nacionalidad</span><strong>{{ us.nationality || '-' }}</strong></div>
            <div class="profile-summary-row"><span>Fecha de nacimiento</span><strong>{{ formatDate(us.birthdate) || '-' }}</strong></div>
            <div class="profile-summary-row"><span>Género</span><strong>{{ us.gender || '-' }}</strong></div>
            <div class="profile-summary-row"><span>Estado civil</span><strong>{{ us.marital_status || '-' }}</strong></div>
            <div class="profile-summary-row"><span>Documento</span><strong>{{ us.type_doc ? us.type_doc + ': ' : '' }}{{ us.n_doc || '-' }}</strong></div>
        </div>

        <div class="settings-widget w-100 profile-summary-card">
            <div class="profile-summary-header">
                <h6 class="profile-summary-title">Ubicación</h6>
                <a href="javascript:void(0);" class="edit-icon-btn" title="Editar ubicación" data-bs-toggle="modal" data-bs-target="#modalUbicacion">
                    <i class="feather-edit-2"></i>
                </a>
            </div>
            <div class="profile-summary-row profile-summary-row-icon"><i class="feather-map-pin"></i><span>{{ ubicacion(us) || 'Sin ubicación registrada' }}</span></div>
        </div>

        <div class="settings-widget w-100 profile-summary-card">
            <div class="profile-summary-header">
                <h6 class="profile-summary-title">Datos de contacto</h6>
                <a href="javascript:void(0);" class="edit-icon-btn" title="Editar datos de contacto" data-bs-toggle="modal" data-bs-target="#modalContacto">
                    <i class="feather-edit-2"></i>
                </a>
            </div>
            <div class="profile-summary-row profile-summary-row-icon"><i class="feather-phone"></i><span>{{ us.n_phone || '-' }}</span></div>
            <div class="profile-summary-row profile-summary-row-icon"><i class="feather-mail"></i><span>{{ us.email || '-' }}</span></div>
        </div>

        <div class="settings-widget w-100 profile-summary-card">
            <h6 class="profile-summary-title">Información adicional</h6>
            <div class="sidebar-menu" style="padding:0">
                <label class="custom_check">
                    <input type="checkbox" v-model="us.availab_travel" @change="saveInfoAdicional('availab_travel')"/><span
                        class="checkmark border-agv"
                    ></span>
                    <span class="form-label-custom">Disponibilidad para viajar</span>
                </label>
                <label class="custom_check">
                    <input type="checkbox" v-model="us.availab_change_resid" @change="saveInfoAdicional('availab_change_resid')"/><span
                        class="checkmark border-agv"
                    ></span>
                    <span class="form-label-custom">Disponibilidad para cambiar de residencia</span>
                </label>
                <label class="custom_check">
                    <input type="checkbox" v-model="us.drivers_license" @change="saveInfoAdicional('drivers_license')"/><span
                        class="checkmark border-agv"
                    ></span>
                    <span class="form-label-custom">Licencia de Conducir</span>
                </label>
                <label class="custom_check">
                    <input type="checkbox" v-model="us.disability" @change="saveInfoAdicional('disability')"/><span
                        class="checkmark border-agv"
                    ></span>
                    <span class="form-label-custom">Discapacidad</span>
                </label>
            </div>
        </div>
    </div>

    <!-- Modal: Datos personales -->
    <div class="modal fade" id="modalDatosPersonales" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Editar datos personales</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="row">
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Nombres <span class="text-danger">*</span></label>
                                <input type="text" class="form-control" v-model="us.name" @input="onlyLettersInput($event, 'name')" required/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Apellido Paterno <span class="text-danger">*</span></label>
                                <input type="text" class="form-control" v-model="us.first_surname" @input="onlyLettersInput($event, 'first_surname')" required/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Apellido Materno <span class="text-danger">*</span></label>
                                <input type="text" class="form-control" v-model="us.second_surname" @input="onlyLettersInput($event, 'second_surname')" required/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Tipo de Documento <span class="text-danger">*</span></label>
                                <vue-select :options="types_docs" id="type_doc_modal" placeholder="Seleccionar" v-model="us.type_doc"/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">N° Documento <span class="text-danger">*</span></label>
                                <input type="text" inputmode="numeric" class="form-control" v-model="us.n_doc" :maxlength="docMaxLength" @input="onlyDigitsDocInput" required/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Nacionalidad <span class="text-danger">*</span></label>
                                <input type="text" class="form-control" v-model="us.nationality" required/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Fecha de Nacimiento <span class="text-danger">*</span></label>
                                <div class="cal-icon">
                                    <datepicker
                                    v-model="us.birthdate"
                                    placeholder="Elegir Fecha"
                                    class="form-control datetimepicker form-label-custom"
                                    :editable="true"
                                    :clearable="false"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Género <span class="text-danger">*</span></label>
                                <vue-select :options="genders" id="gender_modal" placeholder="Seleccionar" v-model="us.gender"/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Estado Civil <span class="text-danger">*</span></label>
                                <vue-select :options="marital_status" id="marital_status_modal" placeholder="Seleccionar" v-model="us.marital_status"/>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
                    <button type="button" class="btn btn-agv-dos" data-bs-dismiss="modal" :disabled="saving" @click="saveDatosPersonales">Actualizar</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal: Ubicación -->
    <div class="modal fade" id="modalUbicacion" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Editar ubicación</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="row">
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">País <span class="text-danger">*</span></label>
                                <vue-select :options="countryOptions" id="country_modal" placeholder="Seleccionar" :model-value="us.country" @update:model-value="onCountry"/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Departamento <span class="text-danger">*</span></label>
                                <vue-select :options="departamentOptions" id="departament_modal" placeholder="Seleccionar" :disabled="!us.country" :model-value="us.departament" @update:model-value="onDepartament"/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Provincia <span class="text-danger">*</span></label>
                                <vue-select :options="provinceOptions" id="province_modal" placeholder="Seleccionar" :disabled="!us.departament" :model-value="us.province" @update:model-value="onProvince"/>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="input-block">
                                <label class="form-label-custom">Distrito <span class="text-danger">*</span></label>
                                <vue-select :options="districtOptions" id="district_modal" placeholder="Seleccionar" :disabled="!us.province" :model-value="us.district" @update:model-value="onDistrict"/>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
                    <button type="button" class="btn btn-agv-dos" data-bs-dismiss="modal" :disabled="saving" @click="saveUbicacion">Actualizar</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal: Datos de contacto -->
    <div class="modal fade" id="modalContacto" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Editar datos de contacto</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="row">
                        <div class="col-md-12">
                            <div class="input-block">
                                <label class="form-label-custom">Celular <span class="text-danger">*</span></label>
                                <input type="text" inputmode="numeric" class="form-control" v-model="us.n_phone" maxlength="9" @input="onlyDigitsPhoneInput" required/>
                            </div>
                        </div>
                        <div class="col-md-12">
                            <div class="input-block">
                                <label class="form-label-custom">Correo</label>
                                <input type="email" class="form-control" :value="us.email" disabled/>
                                <span class="form-label-custom">El correo no se puede editar desde aquí.</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
                    <button type="button" class="btn btn-agv-dos" data-bs-dismiss="modal" :disabled="saving" @click="saveContacto">Actualizar</button>
                </div>
            </div>
        </div>
    </div>
  </template>

  <style scoped>
  .photo-card {
      padding-bottom: 18px;
  }
  .profile-summary-card {
      background: #fff;
      border: 1px solid #e3e8f0;
      border-radius: 12px;
      padding: 16px 18px;
      margin-top: 16px;
  }
  .profile-summary-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
  }
  .profile-summary-title {
      font-weight: 700;
      color: #482775;
      margin-bottom: 0;
      font-size: 14px;
  }
  .edit-icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border-radius: 6px;
      color: #482775;
      cursor: pointer;
  }
  .edit-icon-btn:hover {
      background: #f1ecf8;
  }
  .edit-icon-btn i {
      font-size: 14px;
  }
  .profile-summary-row {
      display: flex;
      justify-content: space-between;
      gap: 10px;
      font-size: 12.5px;
      color: #555;
      padding: 5px 0;
      border-bottom: 1px dashed #eef1f6;
  }
  .profile-summary-row:last-child {
      border-bottom: none;
  }
  .profile-summary-row strong {
      color: #1b1b1b;
      text-align: right;
      font-weight: 600;
  }
  .profile-summary-row-icon {
      justify-content: flex-start;
      gap: 8px;
  }
  .profile-summary-row-icon i {
      flex: none;
      margin-top: 1px;
  }
  .profile-summary-row-icon span {
      min-width: 0;
      overflow-wrap: anywhere;
  }
  </style>
